import { compactFlatten, isString } from '@art-suite/art-core-ts';
import { boolean, number, optional, optionalNumber, optionalString, string, znull, zodLess, ZodLessInfer, zrecord, zunion } from '@kaiizen/zod-less';
import { estimatePostgresJsonbSize, getUniqueId } from './commonLib';

export const ScanTimestampsSchema = zodLess({
  createdAt: optionalNumber, // unix timestamp in milliseconds
  updatedAt: optionalNumber, // unix timestamp in milliseconds
});
export type ScanTimestamps = ZodLessInfer<typeof ScanTimestampsSchema>;

export const CUSTOMER_METADATA_MAX_SIZE = 512;

export const CustomerMetadataSchema = zrecord(zunion(string, number, boolean))
  .refine(
    metadata => estimatePostgresJsonbSize(metadata) <= CUSTOMER_METADATA_MAX_SIZE,
    {
      message: `Customer metadata size must not exceed ${CUSTOMER_METADATA_MAX_SIZE} characters (including keys and values)`,
    },
  );
export type CustomerMetadata = ZodLessInfer<typeof CustomerMetadataSchema>;

export const ScanValueTypeSchema = zunion(number, string, boolean, znull);
export type ScanValueType = ZodLessInfer<typeof ScanValueTypeSchema>;

/** A scan value that is actually present: `ScanValueType` without `null`. */
export type NonNullableScanValueType = NonNullable<ScanValueType>;

export const ScanOrScanTemplatePartTypeSchema = zodLess(
  'scanTemplate|groupTemplate|taskTemplate|optionTemplate|lineItemTemplate|overrideTemplate|scan|group|task|option|lineItem',
);
export type ScanOrScanTemplatePartTypeSchema = ZodLessInfer<typeof ScanOrScanTemplatePartTypeSchema>;
export const ScanOrScanTemplatePartTypes: ScanOrScanTemplatePartTypeSchema[] = [...ScanOrScanTemplatePartTypeSchema.options];

// ************************************************************************
// Scan Template Sku Option Schema
// ************************************************************************
// This is a temporary solution until we have a OrgProduct database table for storing an org's custom SKUs
// However, we may keep this around so we can export/import templates with custom SKUs
export const ScanInlineProductSchema = zodLess({
  id: string.default(() => getUniqueId()), // previously, this was a SKU, now it can be any ID; on exports, this will be an OrgProduct ID
  skus: optional([string]), // list of SKUs to choose from; intended only for line items, sometimes a part has more than one SKU
  title: string,
  productSupplier: optionalString, // name of the product supplier
  productSupplierId: optionalString, // ID of the product supplier
  description: optionalString,
  price: optionalNumber,
  imageUrl: optionalString,
  color: optionalString,
  brand: optionalString, // name of the brand of the product
  customerMetadata: optional(CustomerMetadataSchema),
});
export type ScanInlineProduct = ZodLessInfer<typeof ScanInlineProductSchema>;
/** Inline products keyed by id: the shape scan initialization and edits take to seed a SKU'd part from its product. */
export type InlineProductsById = Record<string, ScanInlineProduct>;

export const getFirstSkuFromScanInlineProduct = (inlineProduct: ScanInlineProduct | string): string | undefined => {
  if (isString(inlineProduct)) return inlineProduct;
  return inlineProduct.skus?.[0];
};
export const getAltSkusFromScanInlineProduct = (inlineProduct: ScanInlineProduct | string): string[] => {
  if (isString(inlineProduct)) return [];
  return inlineProduct.skus?.slice(1) || [];
};
export const getAllSkusFromScanInlineProduct = (inlineProduct: ScanInlineProduct | string): string[] => {
  if (isString(inlineProduct)) return [inlineProduct];
  const result = compactFlatten([inlineProduct.skus]);
  return result;
};
